// Render the quest route as a static PNG (OpenStreetMap tiles + numbered pins +
// arrowed path) entirely on a <canvas>, so it can be inlined into the PDF.
//
// We deliberately don't reuse the live Leaflet map: Leaflet's numbered markers
// are HTML divIcons that DOM/canvas snapshotters can't capture cleanly. Drawing
// the tiles and overlay ourselves gives a self-contained image that mirrors the
// on-screen look (blue route, end arrows, numbered pins). We produce two scales
// — a zoomed-out overview and a tight detail — like the reference screenshot.

import { CARTO_KEY } from './cartoKey';

const TILE = 256;
const ROUTE_COLOR = '#3498db';
const START_COLOR = '#e74c3c';
const PIN_COLOR = '#2f80c4';

function toLatLng(coords) {
  if (!coords) return null;
  if (Array.isArray(coords)) {
    const lat = Number(coords[0]);
    const lng = Number(coords[1]);
    return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
  }
  if (typeof coords === 'object') {
    const lat = Number(coords.lat);
    const lng = Number(coords.lng != null ? coords.lng : coords.lon);
    return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
  }
  return null;
}

// Web-Mercator world-pixel coordinates at a given zoom.
function project(lat, lng, z) {
  const scale = TILE * Math.pow(2, z);
  const x = ((lng + 180) / 360) * scale;
  const s = Math.sin((lat * Math.PI) / 180);
  const y = (0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)) * scale;
  return { x, y };
}

function loadTile(url) {
  return new Promise((resolve) => {
    const img = new Image();
    let done = false;
    const finish = (v) => {
      if (!done) {
        done = true;
        resolve(v);
      }
    };
    img.crossOrigin = 'anonymous'; // OSM sends ACAO:* so the canvas stays exportable
    img.onload = () => finish(img);
    img.onerror = () => finish(null);
    setTimeout(() => finish(null), 8000);
    img.src = url;
  });
}

// Largest zoom at which the bounding box (plus padding) still fits the canvas.
function chooseZoom(bounds, width, height, padding) {
  for (let z = 18; z >= 1; z--) {
    const tl = project(bounds.maxLat, bounds.minLng, z);
    const br = project(bounds.minLat, bounds.maxLng, z);
    const w = Math.abs(br.x - tl.x);
    const h = Math.abs(br.y - tl.y);
    if (w <= width * (1 - 2 * padding) && h <= height * (1 - 2 * padding)) return z;
  }
  return 1;
}

function drawArrow(ctx, a, b) {
  const angle = Math.atan2(b.y - a.y, b.x - a.x);
  const size = 13;
  // Pull the arrow tip back so it sits just before the destination pin.
  const tipX = b.x - Math.cos(angle) * 17;
  const tipY = b.y - Math.sin(angle) * 17;
  ctx.save();
  ctx.fillStyle = ROUTE_COLOR;
  ctx.beginPath();
  ctx.moveTo(tipX, tipY);
  ctx.lineTo(tipX - size * Math.cos(angle - Math.PI / 6), tipY - size * Math.sin(angle - Math.PI / 6));
  ctx.lineTo(tipX - size * Math.cos(angle + Math.PI / 6), tipY - size * Math.sin(angle + Math.PI / 6));
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

function drawPin(ctx, x, y, n, color) {
  const r = 14;
  const cy = y - r - 4; // circle centre; the anchor (tip) lands on (x, y)
  ctx.save();
  ctx.fillStyle = color;
  // Pointer tail.
  ctx.beginPath();
  ctx.moveTo(x - 7, cy + r - 3);
  ctx.lineTo(x + 7, cy + r - 3);
  ctx.lineTo(x, y + 1);
  ctx.closePath();
  ctx.fill();
  // Head.
  ctx.beginPath();
  ctx.arc(x, cy, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.lineWidth = 1.5;
  ctx.strokeStyle = 'rgba(255,255,255,0.85)';
  ctx.stroke();
  // Number.
  ctx.fillStyle = '#fff';
  ctx.font = 'bold 15px -apple-system, "Segoe UI", Arial, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(String(n), x, cy);
  ctx.restore();
}

async function drawMap(points, segments, { width, height, zoom, center }) {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#aadaff';
  ctx.fillRect(0, 0, width, height);

  const c = project(center.lat, center.lng, zoom);
  const originX = c.x - width / 2;
  const originY = c.y - height / 2;
  const maxTile = Math.pow(2, zoom) - 1;
  const subs = ['a', 'b', 'c'];

  const jobs = [];
  for (let tx = Math.floor(originX / TILE); tx <= Math.floor((originX + width) / TILE); tx++) {
    for (let ty = Math.floor(originY / TILE); ty <= Math.floor((originY + height) / TILE); ty++) {
      if (ty < 0 || ty > maxTile) continue;
      const wrappedX = ((tx % (maxTile + 1)) + (maxTile + 1)) % (maxTile + 1);
      const s = subs[Math.abs(tx + ty) % subs.length];
      // Carto Voyager basemap — same style QuestApp renders in gameplay.
      const url = `https://${s}.basemaps.cartocdn.com/rastertiles/voyager/${zoom}/${wrappedX}/${ty}.png?key=${CARTO_KEY}`;
      const px = tx * TILE - originX;
      const py = ty * TILE - originY;
      jobs.push(loadTile(url).then((img) => { if (img) ctx.drawImage(img, px, py, TILE, TILE); }));
    }
  }
  await Promise.all(jobs);

  const toPx = (lat, lng) => {
    const p = project(lat, lng, zoom);
    return { x: p.x - originX, y: p.y - originY };
  };

  segments.forEach((seg) => {
    const a = toPx(seg.from.lat, seg.from.lng);
    const b = toPx(seg.to.lat, seg.to.lng);
    ctx.save();
    ctx.strokeStyle = ROUTE_COLOR;
    ctx.lineWidth = 5;
    ctx.lineCap = 'round';
    ctx.globalAlpha = 0.85;
    if (seg.dashed) ctx.setLineDash([10, 10]);
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(b.x, b.y);
    ctx.stroke();
    ctx.restore();
    drawArrow(ctx, a, b);
  });

  points.forEach((p) => {
    const { x, y } = toPx(p.lat, p.lng);
    drawPin(ctx, x, y, p.n, p.n === 1 ? START_COLOR : PIN_COLOR);
  });

  try {
    return canvas.toDataURL('image/png');
  } catch (e) {
    // Tainted canvas (a tile without CORS) — skip the map rather than fail export.
    return null;
  }
}

// Returns { overview, detailed } data URIs, or null if there are no coordinates.
export async function renderRouteMaps(locations) {
  const list = locations || [];
  const points = [];
  list.forEach((loc, i) => {
    const ll = toLatLng(loc.coordinates);
    if (ll) points.push({ ...ll, n: i + 1 });
  });
  if (points.length === 0) return null;

  const byUuid = {};
  list.forEach((loc) => {
    if (loc.uuid) byUuid[loc.uuid] = toLatLng(loc.coordinates);
  });

  // Main path: consecutive locations, skipping segments out of branching points
  // (mirrors QuestMap).
  const segments = [];
  for (let i = 0; i < list.length - 1; i++) {
    const from = toLatLng(list[i].coordinates);
    const to = toLatLng(list[i + 1].coordinates);
    if (from && to && list[i].type !== 'pre-branching') {
      segments.push({ from, to, dashed: false });
    }
  }
  // Branch paths (dashed).
  list
    .filter((loc) => loc.type === 'pre-branching' && Array.isArray(loc.branches))
    .forEach((start) => {
      const from = toLatLng(start.coordinates);
      start.branches.forEach((br) => {
        if (br && br.next_location_uuid && br.next_location_uuid !== 'END') {
          const to = byUuid[br.next_location_uuid];
          if (from && to) segments.push({ from, to, dashed: true });
        }
      });
    });

  const lats = points.map((p) => p.lat);
  const lngs = points.map((p) => p.lng);
  const bounds = {
    minLat: Math.min(...lats),
    maxLat: Math.max(...lats),
    minLng: Math.min(...lngs),
    maxLng: Math.max(...lngs),
  };
  const center = {
    lat: (bounds.minLat + bounds.maxLat) / 2,
    lng: (bounds.minLng + bounds.maxLng) / 2,
  };

  const W = 1000;
  const H = 720;
  const detailedZoom = chooseZoom(bounds, W, H, 0.12);
  const overviewZoom = Math.max(1, detailedZoom - 2);

  const [overview, detailed] = await Promise.all([
    drawMap(points, segments, { width: W, height: H, zoom: overviewZoom, center }),
    drawMap(points, segments, { width: W, height: H, zoom: detailedZoom, center }),
  ]);

  if (!overview && !detailed) return null;
  return { overview, detailed };
}
